import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  AudioLines,
  BookOpen,
  Check,
  ChevronUp,
  CircleGauge,
  Coins,
  Clock3,
  Fish,
  History,
  LogIn,
  LogOut,
  Radio,
  Settings,
  Share2,
  ShieldCheck,
  Sparkles,
  Waves,
  WalletCards,
  X,
  Zap,
} from 'lucide-react'
import { api, ApiError, socketUrl } from './client/api'
import { authClient } from './client/auth'
import { ArcadeAudio } from './client/audio'
import { formatResultProfitLabel } from './client/resultLabels'
import { ClassicCurrentOverlay } from './game/ClassicCurrentOverlay'
import { OceanStage } from './game/OceanStage'
import { buildSmoothPath } from './game/smoothPath'
import { quoteClassicTileMultiplier } from './shared/gameMath'
import {
  CLASSIC_LOCK_WINDOW_MS,
  CLASSIC_MULTIPLIER_FACTOR,
  MAX_CLASSIC_ACTIVE_TARGETS,
  CLASSIC_ROW_COUNT,
  CLASSIC_ROW_BUFFER,
  CLASSIC_VISIBLE_ROW_COUNT,
  type BetPosition,
  MarketDirection,
  MarketTick,
  type PlayerProfile,
  type PlayerSession,
  type PoolSnapshot,
  type QuestClaim,
  RaidState,
  type ReferralProgress,
  type RoundResult,
  RoundState,
  ServerEvent,
  type SocialQuest,
  SpeciesDefinition,
  type WalletSnapshot,
} from './shared/contracts'

type View = 'lobby' | 'deep-rush' | 'classic-swim' | 'whale-raid' | 'aquadex' | 'community'
type RoundMode = 'deep-rush' | 'classic-swim'

interface LaunchFailure {
  mode: RoundMode
  message: string
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
const number = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const axisTime = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
const CLASSIC_PIXELS_PER_SECOND = 10.8
const CLASSIC_MOBILE_PIXELS_PER_SECOND = 6.8
const CLASSIC_ROW_PITCH = 38
const CLASSIC_MOBILE_ROW_PITCH = 26
const CLASSIC_FULL_GRID_SECONDS = 40
const CLASSIC_THIRTEEN_ROW_SECONDS = 30
const CLASSIC_ELEVEN_ROW_SECONDS = 20
const CLASSIC_SETTLED_TILE_VISIBLE_MS = 2_000
const MARKET_HISTORY_WINDOW_MS = 120_000

function mergeRoundUpdate(current: RoundState | undefined, incoming: RoundState, receivedAt: number): RoundState | undefined {
  if (current && current.id !== incoming.id) return current
  const previousResults = new Map<string, RoundResult>()
  const resultKey = (result: RoundResult) => {
    if (result.selectedTile?.id) return `classic:${result.selectedTile.id}`
    if (result.settlement?.id) return `settlement:${result.settlement.id}`
    if (result.selectedBand) return `rush:${result.selectedBand.index}:${result.entryPrice}:${result.resolvePrice ?? ''}`
    return ''
  }
  for (const result of current?.classicResults ?? []) {
    const key = resultKey(result)
    if (key) previousResults.set(key, result)
  }
  for (const result of current?.rushResults ?? []) {
    const key = resultKey(result)
    if (key) previousResults.set(key, result)
  }

  const preserveResolvedAt = (result: RoundResult) => {
    const previous = previousResults.get(resultKey(result))
    return previous ? { ...result, resolvedAt: previous.resolvedAt } : { ...result, resolvedAt: receivedAt }
  }

  const classicResults = incoming.classicResults?.map(preserveResolvedAt)
  const rushResults = incoming.rushResults?.map(preserveResolvedAt)
  return classicResults || rushResults ? { ...incoming, classicResults, rushResults } : incoming
}

function mergeResolvedRoundResult(current: RoundState | undefined, result: RoundResult, receivedAt: number): RoundState | undefined {
  if (!current || current.id !== result.roundId) return current
  const displayedResult = { ...result, resolvedAt: receivedAt }
  if (result.selectedTile) {
    const classicResults = [
      ...(current.classicResults ?? []).filter((item) => item.selectedTile?.id !== result.selectedTile?.id),
      displayedResult,
    ]
    return { ...current, classicResults }
  }
  if (result.selectedBand) {
    const rushResults = [...(current.rushResults ?? []), displayedResult]
    return { ...current, rushResults, result: displayedResult }
  }
  return current
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function secondsUntil(timestamp: number, now: number): string {
  const seconds = Math.max(0, Math.ceil((timestamp - now) / 1_000))
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

function classicEdgeRowsToTrim(secondsRemaining: number): number {
  if (secondsRemaining < CLASSIC_ELEVEN_ROW_SECONDS) return 3
  if (secondsRemaining < CLASSIC_THIRTEEN_ROW_SECONDS) return 2
  if (secondsRemaining < CLASSIC_FULL_GRID_SECONDS) return 1
  return 0
}

function useSmoothedPriceCamera(targetPrice: number, priceStep: number, reducedMotion: boolean, mobileClassic = false): number {
  const [cameraPrice, setCameraPrice] = useState(targetPrice)
  const targetRef = useRef(targetPrice)

  useEffect(() => {
    targetRef.current = targetPrice
  }, [targetPrice])

  useEffect(() => {
    if (reducedMotion || typeof window === 'undefined') return
    let frame = 0
    let lastTime = performance.now()
    let lastCommit = 0
    const followMs = mobileClassic ? 1_180 : 760
    const minCommitMs = mobileClassic ? 84 : 0
    const maxLag = Math.max(1, priceStep * (mobileClassic ? 1.35 : 0.92))

    const animate = (timestamp: number) => {
      const delta = Math.min(80, Math.max(0, timestamp - lastTime))
      lastTime = timestamp
      const follow = 1 - Math.exp(-delta / followMs)
      if (timestamp - lastCommit < minCommitMs) {
        frame = window.requestAnimationFrame(animate)
        return
      }
      lastCommit = timestamp
      setCameraPrice((current) => {
        const target = targetRef.current
        let next = current + (target - current) * follow
        if (target - next > maxLag) next = target - maxLag
        if (next - target > maxLag) next = target + maxLag
        return Math.abs(next - target) < 0.01 ? target : next
      })
      frame = window.requestAnimationFrame(animate)
    }

    frame = window.requestAnimationFrame(animate)
    return () => window.cancelAnimationFrame(frame)
  }, [priceStep, reducedMotion, mobileClassic])

  return reducedMotion ? targetPrice : cameraPrice
}

function directionMeta(direction: MarketDirection) {
  if (direction === 'up') return { label: 'UP', Icon: ArrowUp }
  if (direction === 'down') return { label: 'DOWN', Icon: ArrowDown }
  return { label: 'RANGE', Icon: Waves }
}

function AppLoader({ error, retry }: { error?: string; retry: () => void }) {
  return (
    <main className="boot-screen" id="main-content">
      <div className="boot-mark"><Fish aria-hidden="true" /></div>
      <p className="eyebrow">PUMPSWIM NETWORK</p>
      <h1>{error ? 'The current is offline.' : 'Mapping the live current…'}</h1>
      <p>{error ?? 'Connecting to the BTC feed and virtual reward pools.'}</p>
      {error && <button className="primary-button" onClick={retry}>Reconnect</button>}
    </main>
  )
}

export function App() {
  const [session, setSession] = useState<PlayerSession | null>()
  const [wallet, setWallet] = useState<WalletSnapshot>()
  const [pools, setPools] = useState<PoolSnapshot>()
  const [openPositions, setOpenPositions] = useState<BetPosition[]>([])
  const [history, setHistory] = useState<BetPosition[]>([])
  const [species, setSpecies] = useState<SpeciesDefinition[]>([])
  const [raid, setRaid] = useState<RaidState>()
  const [tick, setTick] = useState<MarketTick>()
  const [marketHistory, setMarketHistory] = useState<MarketTick[]>([])
  const [marketHealthy, setMarketHealthy] = useState(false)
  const [marketMessage, setMarketMessage] = useState('Connecting to BTC current')
  const [round, setRound] = useState<RoundState>()
  const [view, setView] = useState<View>('lobby')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [bootError, setBootError] = useState<string>()
  const [notice, setNotice] = useState<string>()
  const [launchFailure, setLaunchFailure] = useState<LaunchFailure>()
  const [now, setNow] = useState(Date.now())
  const [sessionAttempt, setSessionAttempt] = useState(0)
  const [stake, setStake] = useState('5')
  const [rewardFlash, setRewardFlash] = useState<{ kind: 'cash' | 'swim' | 'raid'; title: string; amount: string }>()
  const audio = useRef(new ArcadeAudio())
  const autoDeepRestartRound = useRef<string | undefined>(undefined)
  const profile = session?.profile

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 100)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function boot() {
      setBootError(undefined)
      try {
        const referralMatch = location.pathname.match(/^\/r\/([A-Z0-9]{6,32})\/?$/i)
        if (referralMatch) {
          await api.captureReferral(referralMatch[1].toUpperCase())
          window.history.replaceState({}, '', '/')
        }
        const [creatures, raidState, economy] = await Promise.all([api.species(), api.raid(), api.economy()])
        const player = await api.me()
        if (cancelled) return
        setSession(player)
        setWallet(player?.wallet)
        setSpecies(creatures)
        setRaid(raidState)
        setPools(economy)
        if (player) {
          const [positions, settled] = await Promise.all([api.bets('open'), api.bets('settled')])
          if (!cancelled) {
            setOpenPositions(positions)
            setHistory(settled)
          }
        }
      } catch (error) {
        if (!cancelled) setBootError(error instanceof Error ? error.message : 'Could not reach the dive server.')
      }
    }
    void boot()
    return () => { cancelled = true }
  }, [sessionAttempt])

  useEffect(() => {
    let socket: WebSocket | undefined
    let reconnect: number | undefined
    let closed = false
    const connect = () => {
      socket = new WebSocket(socketUrl())
      socket.addEventListener('message', (message) => {
        const event = JSON.parse(message.data as string) as ServerEvent
        const receivedAt = Date.now()
        if (event.type === 'market.tick') {
          setTick(event.payload)
          setMarketHistory((items) => {
            const recent = items
              .filter((item) => item.sequence !== event.payload.sequence && item.sourceTimestamp >= event.payload.sourceTimestamp - MARKET_HISTORY_WINDOW_MS)
            return [...recent, event.payload].sort((left, right) => left.sourceTimestamp - right.sourceTimestamp)
          })
        }
        if (event.type === 'market.history') setMarketHistory(event.payload)
        if (event.type === 'market.status') {
          setMarketHealthy(event.payload.healthy)
          setMarketMessage(event.payload.message)
        }
        if (event.type === 'round.updated') setRound((current) => mergeRoundUpdate(current, event.payload, receivedAt))
        if (event.type === 'round.resolved') {
          setRound((current) => mergeResolvedRoundResult(current, event.payload, receivedAt))
          if (!event.payload.selectedBand) {
          if (!event.payload.selectedTile) setNotice(event.payload.status === 'void' ? 'Round refunded — market feed was not safe to settle.' : event.payload.success ? 'Target captured!' : 'The current escaped your target.')
          }
          audio.current.play(event.payload.success ? 'win' : 'miss')
        }
        if (event.type === 'raid.updated' || event.type === 'raid.checkpoint') setRaid(event.payload)
        if (event.type === 'wallet.updated') setWallet(event.payload)
        if (event.type === 'positions.updated') setOpenPositions(event.payload)
        if (event.type === 'pool.updated') setPools(event.payload)
        if (event.type === 'bet.settled') {
          setHistory((items) => [event.payload, ...items.filter((item) => item.id !== event.payload.id)].slice(0, 50))
          setOpenPositions((items) => items.filter((item) => item.id !== event.payload.id))
        }
        if (event.type === 'jackpot.won') {
          const kind = event.payload.type === 'cash' ? 'cash' : 'swim'
          setRewardFlash({ kind, title: kind === 'cash' ? 'ABYSS CASH JACKPOT' : 'WEEKLY SWIM JACKPOT', amount: event.payload.amount })
          audio.current.play('win')
        }
        if (event.type === 'raid.reward-updated') {
          const reward = event.payload[0]
          if (reward) setRewardFlash({ kind: 'raid', title: 'WHALE RAID REWARD', amount: reward.amount })
        }
      })
      socket.addEventListener('close', () => {
        setMarketHealthy(false)
        setMarketMessage('Reconnecting to the current')
        if (!closed) reconnect = window.setTimeout(connect, 1_500)
      })
    }
    connect()
    return () => {
      closed = true
      if (reconnect) window.clearTimeout(reconnect)
      socket?.close()
    }
  }, [session?.user.id])

  useEffect(() => {
    if (!notice) return
    const timeout = window.setTimeout(() => setNotice(undefined), 3_500)
    return () => window.clearTimeout(timeout)
  }, [notice])

  useEffect(() => {
    if (!rewardFlash) return
    const timeout = window.setTimeout(() => setRewardFlash(undefined), 6_000)
    return () => window.clearTimeout(timeout)
  }, [rewardFlash])

  useEffect(() => {
    if (profile) audio.current.apply(profile.settings)
  }, [profile])

  useEffect(() => {
    if (!profile) return
    const unlockAudio = () => audio.current.unlock(profile.settings)
    window.addEventListener('pointerdown', unlockAudio, { passive: true })
    window.addEventListener('pointerup', unlockAudio, { passive: true })
    window.addEventListener('touchstart', unlockAudio, { passive: true })
    window.addEventListener('touchend', unlockAudio, { passive: true })
    window.addEventListener('click', unlockAudio)
    window.addEventListener('keydown', unlockAudio)
    return () => {
      window.removeEventListener('pointerdown', unlockAudio)
      window.removeEventListener('pointerup', unlockAudio)
      window.removeEventListener('touchstart', unlockAudio)
      window.removeEventListener('touchend', unlockAudio)
      window.removeEventListener('click', unlockAudio)
      window.removeEventListener('keydown', unlockAudio)
    }
  }, [profile])

  const run = async <T,>(task: () => Promise<T>, apply: (result: T) => void) => {
    setBusy(true)
    try {
      apply(await task())
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'The current rejected that move.'
      setNotice(message)
    } finally {
      setBusy(false)
    }
  }

  const refreshSession = async () => {
    const next = await api.me()
    if (!next) throw new ApiError('Your session expired. Sign in again.', 'UNAUTHORIZED', 401)
    const [positions, settled] = await Promise.all([api.bets('open'), api.bets('settled')])
    setSession(next)
    setWallet(next.wallet)
    setOpenPositions(positions)
    setHistory(settled)
  }

  const startRound = async (mode: RoundMode) => {
    if (!profile) {
      setAuthOpen(true)
      return
    }
    if (busy) return
    audio.current.unlock(profile.settings)
    audio.current.play('dive')
    setBusy(true)
    setLaunchFailure(undefined)
    try {
      const nextRound = await api.createRound(mode)
      setRound(nextRound)
      setView(mode)
    } catch (error) {
      const message = error instanceof ApiError && error.code === 'MARKET_UNAVAILABLE'
        ? 'BTC price feed is not ready yet. Wait a few seconds, then try again.'
        : error instanceof ApiError ? error.message : 'Cannot reach the dive server.'
      setLaunchFailure({ mode, message })
      setNotice(message)
    } finally {
      setBusy(false)
    }
  }

  const leaveGame = () => {
    setView('lobby')
    setRound(undefined)
  }

  useEffect(() => {
    if (!profile || view !== 'deep-rush' || !round || round.mode !== 'deep-rush' || round.status !== 'resolved') return
    if (autoDeepRestartRound.current === round.id) return
    autoDeepRestartRound.current = round.id
    const timeout = window.setTimeout(() => {
      void api.createRound('deep-rush')
        .then((nextRound) => {
          setRound(nextRound)
          setView('deep-rush')
        })
        .catch((error) => {
          const message = error instanceof ApiError ? error.message : 'Cannot start the next Deep Rush.'
          setNotice(message)
        })
    }, 2_000)
    return () => window.clearTimeout(timeout)
  }, [profile, round, view])

  if (session === undefined) return <AppLoader error={bootError} retry={() => setSessionAttempt((attempt) => attempt + 1)} />

  const stageMode = view === 'aquadex' || view === 'community' ? 'lobby' : view
  const useLightweightBackground = Boolean(profile) || view !== 'lobby'
  const reducedMotion = Boolean(profile?.settings.reducedMotion) || window.matchMedia('(prefers-reduced-motion: reduce)').matches

  return (
    <div className={`app ${useLightweightBackground ? 'app-lite-stage' : ''} ${profile?.settings.highContrast ? 'high-contrast' : ''}`}>
      {!useLightweightBackground && <OceanStage mode={stageMode} tick={tick} history={marketHistory} round={round} raid={raid} reducedMotion={reducedMotion} frenzy={(profile?.streak ?? 0) >= 3} />}

      <header className="topbar">
        <button className="brand" onClick={leaveGame} aria-label="Return to lobby"><span className="brand-mark"><Fish /></span><span><strong>PUMPSWIM</strong><small>RIDE THE CURRENT</small></span></button>
        <div className="market-chip" aria-live="polite"><span className={`live-dot ${marketHealthy ? 'online' : ''}`} /><span><small>BTC / USD</small><strong>{tick ? money.format(tick.price) : 'SYNCING'}</strong></span><span className="market-state">{marketHealthy ? 'LIVE' : 'HOLD'}</span></div>
        <div className="player-stats">
          {wallet && <><span title="Virtual USD"><WalletCards /> <b>{money.format(Number(wallet.usd.available))}</b></span><span title="SWIM"><Coins /> <b>{number.format(Number(wallet.swim.available))}</b></span></>}
          {profile ? <><button className="icon-button community-nav-button" onClick={() => setView('community')} aria-label="Open community rewards"><Share2 /></button>{session?.user.role === 'admin' && <a className="icon-button admin-nav-button" href="/admin" aria-label="Open admin console"><ShieldCheck /></a>}<button className="icon-button" onClick={() => setSettingsOpen(true)} aria-label="Open settings"><Settings /></button><button className="icon-button" onClick={() => void authClient.signOut().then(() => { setSession(null); setWallet(undefined); setOpenPositions([]); setHistory([]) })} aria-label="Sign out"><LogOut /></button></> : <button className="login-button" onClick={() => setAuthOpen(true)}><LogIn /> SIGN IN</button>}
        </div>
      </header>

      {pools && <PoolHud pools={pools} now={now} />}
      {session && !session.user.emailVerified && <div className="verification-banner"><ShieldCheck /> Verify {session.user.email} before entering a virtual cash game or Raid.</div>}

      <main className="game-shell" id="main-content">
        <TradingRail profile={profile} wallet={wallet} tick={tick} positions={openPositions} history={history} stake={stake} onStake={setStake} marketMessage={marketMessage} onLogin={() => setAuthOpen(true)} />
        <section className="play-space">
          {view === 'lobby' && <Lobby profile={profile} raid={raid} pools={pools} now={now} busy={busy} launchFailure={launchFailure} onStart={(mode) => void startRound(mode)} onRaid={() => setView('whale-raid')} onDex={() => setView('aquadex')} onLogin={() => setAuthOpen(true)} />}
          {(view === 'deep-rush' || view === 'classic-swim') && profile && <RoundArena profile={profile} round={round} tick={tick} history={marketHistory} now={now} busy={busy} onBack={leaveGame} onPredict={(input) => {
            if (!round) return
            audio.current.unlock(profile.settings)
            void run(() => api.predict(round.id, { ...input, stake }), (state) => { setRound(state); audio.current.play('lock') })
          }} />}
          {view === 'whale-raid' && <RaidArena profile={profile} raid={raid} now={now} busy={busy} onBack={leaveGame} onJoin={() => { if (!profile) return setAuthOpen(true); audio.current.unlock(profile.settings); audio.current.play('raid'); void run(() => api.joinRaid(), setRaid) }} onPredict={(checkpoint, direction) => void run(() => api.raidPredict(checkpoint, direction), (state) => { setRaid(state); audio.current.play('lock') })} />}
          {view === 'aquadex' && <AquaDex profile={profile} species={species} onBack={leaveGame} />}
          {view === 'community' && <RewardsHub profile={profile} onBack={leaveGame} onLogin={() => setAuthOpen(true)} />}
        </section>
      </main>

      <nav className="bottom-nav" aria-label="Primary navigation"><button className={view === 'lobby' ? 'active' : ''} onClick={leaveGame}><Waves /><span>Dive</span></button><button className={view === 'whale-raid' ? 'active' : ''} onClick={() => setView('whale-raid')}><ShieldCheck /><span>Raid</span></button><button className={view === 'community' ? 'active' : ''} onClick={() => profile ? setView('community') : setAuthOpen(true)}><Share2 /><span>Rewards</span></button><button className={view === 'aquadex' ? 'active' : ''} onClick={() => setView('aquadex')}><BookOpen /><span>AquaDex</span></button></nav>

      {notice && <div className="toast" role="status"><Radio /> {notice}</div>}
      {rewardFlash && <div className={`reward-flash ${rewardFlash.kind}`} role="status"><Sparkles /><span><small>{rewardFlash.title}</small><strong>+{rewardFlash.amount} {rewardFlash.kind === 'cash' ? '$' : 'SWIM'}</strong></span></div>}
      {settingsOpen && profile && <SettingsPanel profile={profile} busy={busy} onClose={() => setSettingsOpen(false)} onChange={(patch) => void run(() => api.settings(patch), (next) => setSession((current) => current ? { ...current, profile: next } : current))} />}
      {authOpen && <AuthPanel onClose={() => setAuthOpen(false)} onAuthenticated={() => void refreshSession().then(() => setAuthOpen(false)).catch((error) => setNotice(error instanceof Error ? error.message : 'Could not load account'))} />}
      {session?.user.mustChangePassword && <PasswordChangeGate onChanged={() => void refreshSession()} />}
    </div>
  )
}

function PoolHud({ pools, now }: { pools: PoolSnapshot; now: number }) {
  return (
    <section className="pool-hud" aria-label="Live virtual reward pools">
      <div className="cash"><span>ABYSS CASH</span><strong>{money.format(Number(pools.cash.amount))}</strong><small>{pools.cash.winRatePpm} PPM</small></div>
      <div className="swim"><span>WEEKLY SWIM</span><strong>{number.format(Number(pools.weeklySwim.remaining))} SWIM</strong><small>RESET {secondsUntil(pools.weeklySwim.resetAt, now)}</small></div>
      <div className="raid"><span>RAID RESERVOIR</span><strong>{number.format(Number(pools.raid.available))} SWIM</strong><small>NEXT BOSS ≈ {number.format(Number(pools.raid.projectedReward))}</small></div>
    </section>
  )
}

function TradingRail({ profile, wallet, tick, positions, history: trades, stake, onStake, marketMessage, onLogin }: {
  profile?: PlayerProfile
  wallet?: WalletSnapshot
  tick?: MarketTick
  positions: BetPosition[]
  history: BetPosition[]
  stake: string
  onStake: (stake: string) => void
  marketMessage: string
  onLogin: () => void
}) {
  const [tab, setTab] = useState<'open' | 'history'>('open')
  const [mobileOpen, setMobileOpen] = useState(false)
  const rows = tab === 'open' ? positions : trades
  const signedUsd = (value: string | number | undefined) => {
    const amount = Number(value ?? 0)
    return `${amount >= 0 ? '+' : '−'}${Math.abs(amount).toFixed(2)}$`
  }
  return (
    <aside className={`trading-rail ${mobileOpen ? 'mobile-open' : ''}`} aria-label="Trading controls">
      <button className="mobile-trade-toggle" onClick={() => setMobileOpen((open) => !open)}><WalletCards /> {mobileOpen ? 'CLOSE TRADES' : `${money.format(Number(stake || 0))} BET · ${positions.length} OPEN`} <ChevronUp /></button>
      <div className="rail-market"><span className="btc-mark">₿</span><span><small>BTC</small><strong>{tick ? money.format(tick.price) : 'SYNCING'}</strong></span><em>LIVE</em></div>
      <div className="rail-tabs" role="tablist"><button className={tab === 'open' ? 'active' : ''} onClick={() => setTab('open')}>Open Position <b>{positions.length}</b></button><button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}><History /> History</button></div>
      <div className="position-list">
        {rows.length === 0 ? <div className="rail-empty"><Radio /><span>{profile ? tab === 'open' ? 'No open position' : 'No settled trades' : 'Sign in to place virtual bets'}</span></div> : rows.map((position) => (
          <article className={`position-row ${position.status}`} key={position.id}>
            <div>
              <strong>{position.mode === 'classic-swim' ? 'CLASSIC' : 'DEEP RUSH'}</strong>
              <small>{new Date(position.createdAt).toLocaleTimeString()}</small>
            </div>
            <div>
              <small>{position.pnl !== undefined ? 'P&L' : 'STAKE'}</small>
              <b>{position.pnl !== undefined ? signedUsd(position.pnl) : money.format(Number(position.stake))}</b>
              <span>{position.pnl !== undefined ? `${position.multiplier.toFixed(2)}× · payout ${money.format(Number(position.payout))}` : `${position.multiplier.toFixed(2)}× · pays ${money.format(Number(position.possiblePayout))}`}</span>
            </div>
          </article>
        ))}
      </div>
      <div className="bet-composer">
        <div className="rail-balance"><span>Available</span><strong>{wallet ? money.format(Number(wallet.usd.available)) : '$—'}</strong><small>{wallet ? `${money.format(Number(wallet.usd.locked))} locked` : marketMessage}</small></div>
        {profile ? <><label htmlFor="bet-size">BET SIZE <span>VIRTUAL USD</span></label><div className="stake-input"><span>$</span><input id="bet-size" inputMode="decimal" value={stake} onChange={(event) => onStake(event.target.value.replace(/[^0-9.]/g, ''))} aria-label="Bet size in virtual USD" /></div><div className="stake-presets">{['1', '5', '10', '50', '100'].map((value) => <button className={stake === value ? 'active' : ''} key={value} onClick={() => onStake(value)}>${value}</button>)}</div></> : <button className="primary-button rail-login" onClick={onLogin}><LogIn /> SIGN IN TO PLAY</button>}
        <p className="virtual-note"><ShieldCheck /> Virtual assets only · no deposit or cash-out</p>
      </div>
    </aside>
  )
}

function AuthPanel({ onClose, onAuthenticated }: { onClose: () => void; onAuthenticated: () => void }) {
  const token = new URLSearchParams(location.search).get('token')
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'reset'>(token ? 'reset' : 'login')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const resendExistingVerification = async () => {
    const result = await api.resendVerification(email, location.origin)
    if (result.sent) {
      return 'That email already has an unverified account. We sent a fresh verification link.'
    }
    if (result.cooldown && result.nextAllowedAt) {
      const minutes = Math.max(1, Math.ceil((result.nextAllowedAt - Date.now()) / 60_000))
      return `Verification email already sent. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
    }
    return undefined
  }
  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setMessage('')
    try {
      if (mode === 'login') {
        const result = await authClient.signIn.email({ email, password })
        if (result.error) throw new Error(result.error.message)
        onAuthenticated()
      } else if (mode === 'register') {
        const result = await authClient.signUp.email({ name, email, password, callbackURL: location.origin })
        if (result.error) {
          const resendMessage = await resendExistingVerification()
          throw new Error(resendMessage ?? result.error.message)
        }
        setMessage('Account created. Check your email to verify it before playing.')
        onAuthenticated()
      } else if (mode === 'forgot') {
        const result = await authClient.requestPasswordReset({ email, redirectTo: location.origin })
        if (result.error) throw new Error(result.error.message)
        setMessage('If that account exists, a reset link is on its way.')
      } else if (token) {
        const result = await authClient.resetPassword({ newPassword: password, token })
        if (result.error) throw new Error(result.error.message)
        history.replaceState({}, '', location.pathname)
        setMode('login')
        setMessage('Password reset. Sign in with your new password.')
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Authentication failed')
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <div className="modal-scrim auth-scrim" role="presentation">
      <section className="auth-panel" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="icon-button auth-close" onClick={onClose} aria-label="Close"><X /></button>
        <p className="eyebrow">SECURE DIVE ACCESS</p><h2 id="auth-title">{mode === 'login' ? 'Welcome back, diver.' : mode === 'register' ? 'Create your dive account.' : mode === 'forgot' ? 'Reset your password.' : 'Choose a new password.'}</h2>
        <p className="auth-copy">Every new verified account receives <strong>10,000 virtual USD</strong> exactly once.</p>
        {mode !== 'forgot' && mode !== 'reset' && <button className="google-button" disabled={submitting} onClick={() => void authClient.signIn.social({ provider: 'google', callbackURL: location.origin })}><span>G</span> Continue with Google</button>}
        <form onSubmit={(event) => void submit(event)}>
          {mode === 'register' && <label>DISPLAY NAME<input required minLength={2} value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" /></label>}
          {mode !== 'reset' && <label>EMAIL<input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" /></label>}
          {mode !== 'forgot' && <label>{mode === 'reset' ? 'NEW PASSWORD' : 'PASSWORD'}<input required type="password" minLength={mode === 'login' ? 8 : 12} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} /></label>}
          {message && <p className="auth-message" role="status">{message}</p>}
          <button className="primary-button" disabled={submitting}>{submitting ? 'CONNECTING…' : mode === 'login' ? 'SIGN IN' : mode === 'register' ? 'CREATE ACCOUNT' : mode === 'forgot' ? 'SEND RESET LINK' : 'RESET PASSWORD'}</button>
        </form>
        <div className="auth-switch">{mode === 'login' ? <><button onClick={() => setMode('forgot')}>Forgot password?</button><button onClick={() => setMode('register')}>Create account</button></> : <button onClick={() => setMode('login')}>Back to sign in</button>}</div>
      </section>
    </div>
  )
}

function PasswordChangeGate({ onChanged }: { onChanged: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState('')
  return (
    <div className="modal-scrim force-password"><form className="auth-panel" onSubmit={(event) => { event.preventDefault(); setError(''); void api.changeTemporaryPassword(currentPassword, newPassword).then(onChanged).catch((reason) => setError(reason instanceof Error ? reason.message : 'Password change failed')) }}><p className="eyebrow">ADMIN-CREATED ACCOUNT</p><h2>Replace your temporary password.</h2><p className="auth-copy">You must secure this account before entering a cash game or Raid.</p><label>CURRENT PASSWORD<input type="password" required minLength={8} value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label><label>NEW PASSWORD<input type="password" required minLength={12} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>{error && <p className="auth-message">{error}</p>}<button className="primary-button">SECURE ACCOUNT</button></form></div>
  )
}

function Lobby({ profile, raid, pools, now, busy, launchFailure, onStart, onRaid, onDex, onLogin }: {
  profile?: PlayerProfile
  raid?: RaidState
  pools?: PoolSnapshot
  now: number
  busy: boolean
  launchFailure?: LaunchFailure
  onStart: (mode: RoundMode) => void
  onRaid: () => void
  onDex: () => void
  onLogin: () => void
}) {
  return (
    <div className="lobby-panel">
      <div className="lobby-copy">
        <p className="eyebrow"><Sparkles /> LIVE BTC ARCADE · VIRTUAL REWARDS</p>
        <h1>READ THE CURRENT.<br /><span>HUNT THE ABYSS.</span></h1>
        <p>Trade the live BTC current with virtual USD, earn SWIM, and collect creatures. No deposit, cash-out, NFT or real-money prize.</p>
        <button className="primary-button" disabled={busy} onClick={() => profile ? onStart('classic-swim') : onLogin()}><CircleGauge /> {busy ? 'MAPPING CURRENT…' : profile ? 'PLAY CLASSIC SWIM' : 'SIGN IN & PLAY'} <span>{profile ? '$1–$100' : '10K VIRTUAL USD'}</span></button>
        {launchFailure && (
          <div className="launch-failure" role="alert">
            <Radio aria-hidden="true" />
            <span><strong>DIVE LINK INTERRUPTED</strong><small>{launchFailure.message}</small></span>
            <button disabled={busy} onClick={() => onStart(launchFailure.mode)}>TRY AGAIN</button>
          </div>
        )}
      </div>

      <div className="mode-grid">
        <button className="mode-card deep" onClick={() => profile ? onStart('deep-rush') : onLogin()} disabled={busy}>
          <span className="mode-icon"><Zap /></span>
          <span><small>20 SECOND SKILL RUN</small><strong>DEEP RUSH</strong><em>Tap Up or Down. Multiple entries before lock.</em></span>
          <ArrowRight />
        </button>
        <button className="mode-card classic" onClick={() => profile ? onStart('classic-swim') : onLogin()} disabled={busy}>
          <span className="mode-icon"><CircleGauge /></span>
          <span><small>PRICE × TIME GRID</small><strong>CLASSIC SWIM</strong><em>Choose a sonar tile. Distant targets pay more.</em></span>
          <ArrowRight />
        </button>
        <button className="mode-card raid" onClick={onRaid}>
          <span className="mode-icon"><ShieldCheck /></span>
          <span><small>{raid?.phase === 'active' ? 'RAID LIVE NOW' : `NEXT BREACH ${raid ? secondsUntil(raid.checkInAt, now) : 'SYNCING'}`}</small><strong>WHALE RAID</strong><em>{pools ? `Projected reward ${number.format(Number(pools.raid.projectedReward))} SWIM` : 'Three calls. One shared boss.'}</em></span>
          <ArrowRight />
        </button>
      </div>

      <button className="dex-strip" onClick={onDex}>
        <span><BookOpen /> AQUADEX</span>
        <strong>{Object.keys(profile?.collection ?? {}).length}<small>/12 SPECIES</small></strong>
        <span className="mini-progress"><i style={{ width: `${Object.keys(profile?.collection ?? {}).length / 12 * 100}%` }} /></span>
        <ArrowRight />
      </button>
    </div>
  )
}

function RoundArena({ profile, round, tick, history, now, busy, onBack, onPredict }: {
  profile: PlayerProfile
  round?: RoundState
  tick?: MarketTick
  history: MarketTick[]
  now: number
  busy: boolean
  onBack: () => void
  onPredict: (input: { bandIndex?: number; tileId?: string }) => void
}) {
  if (!round) return <ArenaLoading onBack={onBack} />
  const selecting = round.status === 'selecting'
  const remaining = secondsUntil(round.resolveAt, now)
  const lockRemaining = secondsUntil(round.lockAt, now)

  return (
    <div className={`arena-panel ${round.mode}`}>
      <div className="arena-heading">
        <button className="icon-button" onClick={onBack} aria-label="Exit round"><ArrowLeft /></button>
        <div><p className="eyebrow">{round.mode === 'deep-rush' ? 'DEEP RUSH' : 'CLASSIC SWIM'}</p><h1>{round.mode === 'deep-rush' ? (selecting ? 'LOCK YOUR CURRENT' : 'RIDE IT OUT') : 'MULTI-TARGET LIVE'}</h1></div>
        {round.mode !== 'classic-swim' && <div className="round-clock"><Clock3 /><strong>{remaining}</strong><small>{selecting ? `LOCK ${lockRemaining}` : 'RESOLVE'}</small></div>}
      </div>

      {round.mode === 'deep-rush' ? (
        <DeepRushTargets round={round} tick={tick} history={history} now={now} busy={busy} onPredict={onPredict} />
      ) : (
        <ClassicGrid round={round} tick={tick} history={history} now={now} busy={busy} reducedMotion={profile.settings.reducedMotion} frenzy={profile.streak >= 3} onPredict={onPredict} />
      )}

    </div>
  )
}

function DeepRushTargets({ round, tick, history, now, busy, onPredict }: {
  round: RoundState
  tick?: MarketTick
  history: MarketTick[]
  now: number
  busy: boolean
  onPredict: (input: { bandIndex?: number }) => void
}) {
  const deepFieldRef = useRef<HTMLDivElement>(null)
  const [deepFieldSize, setDeepFieldSize] = useState({ width: 1_000, height: 348 })
  useEffect(() => {
    const node = deepFieldRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const updateSize = () => {
      const rect = node.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        setDeepFieldSize((current) => (
          Math.abs(current.width - rect.width) < 0.5 && Math.abs(current.height - rect.height) < 0.5
            ? current
            : { width: rect.width, height: rect.height }
        ))
      }
    }
    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  const locked = now >= round.lockAt
  const rushPredictions = round.rushPredictions?.length ? round.rushPredictions : round.prediction ? [round.prediction] : []
  const entryCountByBand = rushPredictions.reduce((counts, prediction) => {
    if (typeof prediction.bandIndex === 'number') counts.set(prediction.bandIndex, (counts.get(prediction.bandIndex) ?? 0) + 1)
    return counts
  }, new Map<number, number>())
  const selectedBand = round.bands.find((band) => band.index === rushPredictions.at(-1)?.bandIndex)
  const selectedBands = round.bands.filter((band) => entryCountByBand.has(band.index))
  const playableBands = round.bands.filter((band) => band.direction !== 'range')
  const currentPrice = tick?.price ?? round.entryPrice
  const centerIndex = 3
  const priceToOffset = (price: number) => {
    const rowsFromEntry = (round.entryPrice - price) / round.bandSize
    return Math.max(-centerIndex, Math.min(centerIndex, rowsFromEntry)) * 54
  }
  const currentOffset = priceToOffset(currentPrice)
  const targetOffset = selectedBand ? priceToOffset(selectedBand.center) : 0
  const entryOffset = priceToOffset(round.entryPrice)
  const isWinningDirection = selectedBand
    ? selectedBand.direction === 'down'
      ? currentPrice <= selectedBand.center
      : selectedBand.direction === 'up'
        ? currentPrice >= selectedBand.center
        : currentPrice >= selectedBand.min && currentPrice <= selectedBand.max
    : false
  const currentVsTarget = selectedBand
    ? isWinningDirection
      ? `${selectedBand.direction === 'down' ? 'below' : 'above'} target · win zone`
      : `${selectedBand.direction === 'down' ? 'above' : 'below'} target`
    : 'choose up/down'
  const currentStateClass = selectedBand
    ? isWinningDirection
      ? 'inside'
      : selectedBand.direction === 'down'
        ? 'above'
        : 'below'
    : 'idle'
  const deepChartWidth = deepFieldSize.width
  const deepChartHeight = deepFieldSize.height
  const deepPlayheadX = deepChartWidth * 0.34
  const roundDurationMs = Math.max(1, round.resolveAt - round.startedAt)
  const resolveRemainingRatio = Math.max(0, Math.min(1, (round.resolveAt - now) / roundDurationMs))
  const resolveLineX = deepPlayheadX + (deepChartWidth - deepPlayheadX) * resolveRemainingRatio
  const resolveTouched = now >= round.resolveAt
  const showResolveLine = now - round.resolveAt < 2_000
  const trailWindowMs = Math.max(round.rushRules?.roundMs ?? round.resolveAt - round.startedAt, 20_000)
  const latestTimestamp = tick?.sourceTimestamp ?? history.at(-1)?.sourceTimestamp ?? now
  const trailTicks = [
    ...history.filter((item) => latestTimestamp - item.sourceTimestamp <= trailWindowMs),
    ...(tick ? [tick] : []),
  ].filter((item, index, items) => index === 0 || item.sourceTimestamp !== items[index - 1].sourceTimestamp)
  const trailPoints = trailTicks.map((item) => ({
    x: deepPlayheadX - ((latestTimestamp - item.sourceTimestamp) / trailWindowMs) * deepPlayheadX,
    y: deepChartHeight / 2 + priceToOffset(item.price),
  }))
  if (trailPoints.length === 0) trailPoints.push({ x: deepPlayheadX, y: deepChartHeight / 2 + currentOffset })
  if (trailPoints.length === 1) trailPoints.unshift({ x: Math.max(0, deepPlayheadX - 18), y: trailPoints[0].y })
  const trailPath = buildSmoothPath(trailPoints)
  const serverRushResult = [...(round.rushResults ?? [])]
    .filter((result) => result.resolvedAt && now - result.resolvedAt < 2_400)
    .sort((left, right) => (right.resolvedAt ?? 0) - (left.resolvedAt ?? 0))[0]
  const optimisticRushResult = !serverRushResult && resolveTouched && rushPredictions.length
    ? rushPredictions.reduce<RoundResult | undefined>((summary, prediction) => {
      const band = round.bands.find((item) => item.index === prediction.bandIndex)
      if (!band) return summary
      const stake = Number(prediction.stake ?? 0)
      const multiplier = prediction.lockedMultiplier ?? band.multiplier
      const hit = band.direction === 'down'
        ? currentPrice <= band.center
        : band.direction === 'up'
          ? currentPrice >= band.center
          : currentPrice >= band.min && currentPrice <= band.max
      const profit = (summary?.profit ?? 0) + (hit ? stake * (multiplier - 1) : -stake)
      return {
        roundId: round.id,
        status: 'resolved',
        success: profit > 0,
        entryPrice: round.entryPrice,
        resolvePrice: currentPrice,
        selectedBand: band,
        multiplier,
        xpAwarded: 0,
        streak: 0,
        frenzy: false,
        insight: 'Awaiting server settlement.',
        resolvedAt: round.resolveAt,
        profit,
      }
    }, undefined)
    : undefined
  const latestRushResult = serverRushResult ?? optimisticRushResult
  const latestRushProfitLabel = formatResultProfitLabel(latestRushResult)
  return (
    <div className="deep-rush-board" aria-label="Deep Rush price and time target map">
      <div className="deep-chart" style={{
        '--deep-current-y': `${currentOffset.toFixed(2)}px`,
        '--deep-entry-y': `${entryOffset.toFixed(2)}px`,
        '--deep-target-y': `${targetOffset.toFixed(2)}px`,
        '--deep-playhead-x': `${deepPlayheadX.toFixed(2)}px`,
        '--deep-resolve-x': `${resolveLineX.toFixed(2)}px`,
      } as CSSProperties}>
        <div className="deep-time-axis" aria-hidden="true">
          <span>{axisTime.format(round.startedAt)}</span>
          <span>LOCK {axisTime.format(round.lockAt)}</span>
          <span>{axisTime.format(round.resolveAt)}</span>
        </div>
        <div className="deep-price-lanes" aria-hidden="true">
          {round.bands.map((band) => {
            const offset = priceToOffset(band.center)
            const selected = entryCountByBand.has(band.index)
            const entryCount = entryCountByBand.get(band.index) ?? 0
            return (
              <span key={band.index} className={`deep-price-lane ${band.direction} ${selected ? 'selected' : ''}`} style={{ '--lane-y': `${offset.toFixed(2)}px` } as CSSProperties}>
                <i>{money.format(band.center)}{entryCount > 1 ? ` ×${entryCount}` : ''}</i>
              </span>
            )
          })}
        </div>
        <div className="deep-entry-line" aria-hidden="true"><span>ENTRY {money.format(round.entryPrice)}</span></div>
        {showResolveLine && <div className={`deep-resolve-line ${resolveTouched ? 'settled' : ''}`} aria-hidden="true"><span>END {secondsUntil(round.resolveAt, now)}</span></div>}
        <div className="deep-history-layer" ref={deepFieldRef} aria-hidden="true">
          <svg className="deep-history-svg" viewBox={`0 0 ${deepChartWidth} ${deepChartHeight}`} preserveAspectRatio="none">
            <path className="deep-history-line" d={trailPath} />
          </svg>
          <img
            className="deep-current-rider"
            src="/assets/current-rider-fish.png"
            alt=""
            style={{ '--rider-y': `${currentOffset.toFixed(2)}px` } as CSSProperties}
          />
          {latestRushProfitLabel && (
            <div
              key={`${latestRushResult?.selectedBand?.index}:${latestRushResult?.resolvedAt}`}
              className={`deep-profit-pop ${latestRushResult?.status === 'void' ? 'void' : latestRushResult?.success ? 'win' : 'miss'}`}
              style={{ '--rider-y': `${currentOffset.toFixed(2)}px` } as CSSProperties}
              role="status"
            >
              {latestRushProfitLabel}
            </div>
          )}
        </div>
        <div className="deep-price-axis" aria-label="Deep Rush realtime price axis">
          <span className="deep-axis-unit">USD</span>
          {round.bands.map((band) => {
            const offset = priceToOffset(band.center)
            return <span key={band.index} className="deep-axis-tick" style={{ '--axis-y': `${offset.toFixed(2)}px` } as CSSProperties}>{number.format(band.center)}</span>
          })}
          {selectedBands.map((band) => {
            const offset = priceToOffset(band.center)
            const entryCount = entryCountByBand.get(band.index) ?? 1
            return (
              <output key={band.index} className={`deep-target-axis-tag ${band.direction}`} style={{ '--axis-y': `${offset.toFixed(2)}px` } as CSSProperties} aria-live="polite">
                <span>TARGET{entryCount > 1 ? ` ×${entryCount}` : ''}</span><strong>{money.format(band.center)}</strong><small>{band.direction}</small>
              </output>
            )
          })}
          <output className={`deep-current-axis-tag ${currentStateClass}`} style={{ '--axis-y': `${currentOffset.toFixed(2)}px` } as CSSProperties} aria-live="polite">
            <span>LIVE</span><strong>{money.format(currentPrice)}</strong><small>{currentVsTarget}</small>
          </output>
        </div>
      </div>

      <div className="deep-targets" aria-label="Price band targets">
        {[...playableBands].reverse().map((band) => {
          const selected = rushPredictions.some((prediction) => prediction.bandIndex === band.index)
          const entryCount = entryCountByBand.get(band.index) ?? 0
          const { Icon } = directionMeta(band.direction)
          return (
            <button key={band.index} className={`target-band ${selected ? 'selected' : ''} ${band.direction}`} disabled={busy || locked} onClick={() => onPredict({ bandIndex: band.index })}>
              <span><Icon /> {band.direction}</span>
              <b>{money.format(band.center)}</b>
              <strong>{entryCount > 0 ? `×${entryCount}` : `${band.multiplier.toFixed(2)}×`}</strong>
            </button>
          )
        })}
        <p className="deep-lock-note"><Zap /> Tap Up/Down multiple times before lock. Each tap creates one entry.</p>
      </div>
    </div>
  )
}

function ClassicGrid({ round, tick, history, now, busy, reducedMotion, frenzy, onPredict }: {
  round: RoundState
  tick?: MarketTick
  history: MarketTick[]
  now: number
  busy: boolean
  reducedMotion: boolean
  frenzy: boolean
  onPredict: (input: { tileId?: string }) => void
}) {
  const [compactClassic, setCompactClassic] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px), (max-width: 980px) and (max-height: 520px)').matches)
  useEffect(() => {
    const query = window.matchMedia('(max-width: 760px), (max-width: 980px) and (max-height: 520px)')
    const update = () => setCompactClassic(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  const columns = useMemo(() => [...new Set((round.classicTiles ?? []).map((tile) => tile.column))].map((column) => ({
    column,
    tiles: (round.classicTiles ?? []).filter((tile) => tile.column === column).sort((a, b) => a.index - b.index),
  })), [round.classicTiles])
  const settledResultByTile = useMemo(() => new Map((round.classicResults ?? [])
    .filter((result) => result.selectedTile)
    .map((result) => [result.selectedTile!.id, result])), [round.classicResults])
  const visibleResultByTile = useMemo(() => new Map((round.classicResults ?? [])
    .filter((result) => result.selectedTile && result.resolvedAt && now - result.resolvedAt < CLASSIC_SETTLED_TILE_VISIBLE_MS)
    .map((result) => [result.selectedTile!.id, result])), [now, round.classicResults])
  const predictionByTile = useMemo(() => new Map((round.classicPredictions ?? []).map((prediction) => [prediction.tileId, prediction])), [round.classicPredictions])
  const activeCount = (round.classicPredictions ?? []).filter((prediction) => !settledResultByTile.has(prediction.tileId ?? '')).length
  const fadeWindowMs = round.classicRules?.lockWindowMs ?? CLASSIC_LOCK_WINDOW_MS
  const maxActiveTargets = round.classicRules?.maxActiveTargets ?? MAX_CLASSIC_ACTIVE_TARGETS
  const multiplierFactor = round.classicRules?.multiplierFactor ?? CLASSIC_MULTIPLIER_FACTOR
  const priceStep = round.classicRules?.priceStep ?? round.bandSize
  const centerIndex = Math.floor(CLASSIC_ROW_COUNT / 2)
  const currentPrice = tick?.price ?? round.entryPrice
  const priceCamera = useSmoothedPriceCamera(currentPrice, priceStep, reducedMotion, compactClassic)
  const priceAnchor = Math.round(priceCamera / priceStep) * priceStep
  const rowPitch = compactClassic ? CLASSIC_MOBILE_ROW_PITCH : CLASSIC_ROW_PITCH
  const priceAxisTicks = [0, 4, 8, 12].map((visibleIndex) => {
    const index = CLASSIC_ROW_BUFFER + visibleIndex
    return {
      id: `axis:${index}:${priceAnchor}`,
      index,
      center: priceAnchor + (centerIndex - index) * priceStep,
    }
  })
  const currentPriceRows = clamp((priceCamera - currentPrice) / priceStep, -1.15, 1.15)
  const currentPriceStyle = { '--price-axis-offset': `${(currentPriceRows * rowPitch).toFixed(2)}px` } as CSSProperties
  const latestResult = [...(round.classicResults ?? [])]
    .filter((result) => result.resolvedAt && now - result.resolvedAt < CLASSIC_SETTLED_TILE_VISIBLE_MS)
    .sort((left, right) => (right.resolvedAt ?? 0) - (left.resolvedAt ?? 0))[0]
  const visualNow = reducedMotion ? Math.floor(now / 5_000) * 5_000 : now
  const pixelsPerSecond = compactClassic ? CLASSIC_MOBILE_PIXELS_PER_SECOND : CLASSIC_PIXELS_PER_SECOND
  const columnPitch = pixelsPerSecond * 5
  const gridYOffset = ((priceCamera - priceAnchor) / priceStep) * rowPitch
  const wrapStyle = {
    '--classic-grid-y': `${gridYOffset.toFixed(2)}px`,
    '--classic-grid-line-y': `${(gridYOffset - rowPitch * (CLASSIC_VISIBLE_ROW_COUNT / 2)).toFixed(2)}px`,
    '--classic-column-pitch': `${columnPitch.toFixed(2)}px`,
  } as CSSProperties
  const priceGridLines = Array.from({ length: CLASSIC_VISIBLE_ROW_COUNT + 1 }, (_, index) => ({
    id: `price:${index}`,
    offset: gridYOffset + (index - CLASSIC_VISIBLE_ROW_COUNT / 2) * rowPitch,
  }))
  const firstResolveAt = round.startedAt + 10_000
  const firstVisibleGridIndex = Math.floor((visualNow - firstResolveAt) / 5_000) - 4
  const timeGridLines = Array.from({ length: 32 }, (_, index) => {
    const gridIndex = firstVisibleGridIndex + index
    const timestamp = firstResolveAt + gridIndex * 5_000
    return {
      id: `time:${gridIndex}`,
      offset: ((timestamp - visualNow) / 1_000) * pixelsPerSecond,
    }
  })
  const axisAnchor = Math.floor(visualNow / 10_000) * 10_000
  const timeAxisTicks = Array.from({ length: 17 }, (_, index) => axisAnchor + (index - 6) * 10_000)
    .filter((timestamp) => Math.abs(timestamp - visualNow) > 4_000)
  return (
    <div className={`classic-wrap ${compactClassic ? 'compact' : ''} ${reducedMotion ? 'reduced' : ''}`} style={wrapStyle}>
      <div className="classic-water-grid" aria-hidden="true">
        {timeGridLines.map((line) => (
          <span key={line.id} className="classic-grid-line vertical" style={{ '--grid-line-x': `${line.offset.toFixed(2)}px` } as CSSProperties} />
        ))}
        {priceGridLines.map((line) => (
          <span key={line.id} className="classic-grid-line horizontal" style={{ '--grid-line-y': `${line.offset.toFixed(2)}px` } as CSSProperties} />
        ))}
      </div>
      <div className="classic-grid" aria-label="Price and time target grid">
        {columns.map(({ column, tiles }) => {
          const secondsRemaining = ((tiles[0]?.resolveAt ?? visualNow) - visualNow) / 1_000
          const fadeWindowSeconds = fadeWindowMs / 1_000
          const playheadFade = clamp(secondsRemaining - Math.max(0, fadeWindowSeconds - 1), 0, 1)
          const edgeRowsToTrim = classicEdgeRowsToTrim(secondsRemaining)
          const columnHasTrackedTile = tiles.some((tile) => {
            const settled = settledResultByTile.has(tile.id)
            const pending = predictionByTile.has(tile.id) && !settled
            return pending || visibleResultByTile.has(tile.id)
          })
          const centerTile = tiles.find((tile) => tile.index === centerIndex) ?? tiles[Math.floor(tiles.length / 2)]
          const columnPriceStep = tiles[0] ? tiles[0].max - tiles[0].min : priceStep
          const columnPriceAnchor = centerTile?.center ?? priceCamera
          const columnPriceOffset = ((priceCamera - columnPriceAnchor) / columnPriceStep) * rowPitch
          const cameraRowShift = Math.round(columnPriceOffset / rowPitch)
          const visibleMinIndex = clamp(CLASSIC_ROW_BUFFER + edgeRowsToTrim - cameraRowShift, 0, CLASSIC_ROW_COUNT - 1)
          const visibleMaxIndex = clamp(CLASSIC_ROW_BUFFER + CLASSIC_VISIBLE_ROW_COUNT - 1 - edgeRowsToTrim - cameraRowShift, 0, CLASSIC_ROW_COUNT - 1)
          const columnStyle = {
            '--column-offset': `${secondsRemaining * pixelsPerSecond + columnPitch}px`,
            '--column-y-offset': `${columnPriceOffset.toFixed(2)}px`,
            '--tile-arrival-opacity': playheadFade.toFixed(3),
            '--selected-arrival-opacity': '1',
          } as CSSProperties
          return (
          <div className={`classic-column ${secondsRemaining < -8 && !columnHasTrackedTile ? 'passed' : ''}`} key={column} style={columnStyle}>
            {tiles.map((tile) => {
              const prediction = predictionByTile.get(tile.id)
              const settledResult = settledResultByTile.get(tile.id)
              const result = visibleResultByTile.get(tile.id)
              const selected = Boolean(prediction) && (!settledResult || Boolean(result))
              const pending = selected && !result
              const trimmed = (tile.index < visibleMinIndex || tile.index > visibleMaxIndex) && !selected && !result
              const fadedOut = secondsRemaining <= Math.max(0, fadeWindowSeconds - 1) && !selected && !result
              const profitLabel = formatResultProfitLabel(result)
              const stateClass = result ? `settled ${result.status === 'void' ? 'void' : result.success ? 'win' : 'miss'}` : pending ? 'selected pending' : ''
              const disabled = busy || selected || Boolean(result) || trimmed || fadedOut || activeCount >= maxActiveTargets
              const ariaResult = profitLabel ? `, result ${profitLabel}` : pending ? ', target pending' : ''
              const quotedMultiplier = prediction?.lockedMultiplier ?? result?.selectedTile?.multiplier ?? quoteClassicTileMultiplier(tile, now, multiplierFactor)
              return <button key={tile.id} className={`sonar-tile ${stateClass} ${trimmed || fadedOut ? 'trimmed' : ''} ${tile.direction}`} disabled={disabled} onClick={() => onPredict({ tileId: tile.id })} aria-label={`${tile.direction}, ${money.format(tile.center)}, ${quotedMultiplier.toFixed(2)} times${ariaResult}`}>{profitLabel ? <><strong className="tile-profit">{profitLabel}</strong><small>P/L</small></> : <strong>{quotedMultiplier.toFixed(2)}×</strong>}</button>
            })}
          </div>
        )})}
      </div>
      <ClassicCurrentOverlay tick={tick} history={history} round={round} priceCamera={priceCamera} reducedMotion={reducedMotion} frenzy={frenzy} />
      <div className="classic-time-axis" aria-label="Realtime price target timeline">
        <time className="current-time" style={{ '--time-axis-offset': '0px' } as CSSProperties}>{axisTime.format(now)}</time>
        {timeAxisTicks.map((timestamp) => {
          const secondsRemaining = (timestamp - visualNow) / 1_000
          const style = { '--time-axis-offset': `${secondsRemaining * pixelsPerSecond}px` } as CSSProperties
          return <time key={timestamp} style={style}>{axisTime.format(timestamp)}</time>
        })}
      </div>
      <div className="classic-price-axis" aria-label="Realtime BTC price axis">
        <span className="price-axis-unit">USD</span>
        {priceAxisTicks.map((tile) => {
          const style = { '--price-axis-offset': `${(((priceCamera - tile.center) / priceStep) * rowPitch).toFixed(2)}px` } as CSSProperties
          return <span className="price-axis-tick" style={style} key={tile.id}>{number.format(tile.center)}</span>
        })}
        <output className="current-price-tag" style={currentPriceStyle} aria-live="off">{money.format(currentPrice)}</output>
      </div>
      {latestResult && (
        <div key={`${latestResult.selectedTile?.id}:${latestResult.resolvedAt}`} className={`classic-profit-pop ${latestResult.status === 'void' ? 'void' : latestResult.success ? 'win' : 'miss'}`} role="status">
          {formatResultProfitLabel(latestResult)}
        </div>
      )}
      <p className="classic-hint"><CircleGauge /> {activeCount}/{maxActiveTargets} live · fade {fadeWindowMs / 1_000}s · factor {multiplierFactor.toFixed(2)}×</p>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ResultOverlay({ round, canReplay, onReplay, onExit }: { round: RoundState; canReplay: boolean; onReplay: () => void; onExit: () => void }) {
  const result = round.result!
  const shareable = result.loot && ['rare', 'epic', 'legendary'].includes(result.loot.rarity)
  const shareCatch = async () => {
    if (!result.loot) return
    const deepLink = new URL(location.origin)
    deepLink.searchParams.set('catch', result.loot.id)
    const text = `I caught ${result.loot.name} at ${result.multiplier.toFixed(2)}× in PumpSwim.`
    if (navigator.share) await navigator.share({ title: 'PumpSwim rare catch', text, url: deepLink.toString() })
    else await navigator.clipboard.writeText(`${text} ${deepLink}`)
  }
  return (
    <div className={`result-overlay ${result.success ? 'win' : 'miss'}`} role="dialog" aria-modal="true" aria-labelledby="result-title">
      <div className="result-mark">{result.status === 'void' ? <ShieldCheck /> : result.success ? <Check /> : <X />}</div>
      <p className="eyebrow">{result.status === 'void' ? 'REFUND · MARKET FEED UNSAFE' : result.success ? `${result.multiplier.toFixed(2)}× TARGET CAPTURED` : 'CURRENT ESCAPED'}</p>
      <h2 id="result-title">{result.loot ? `You found ${result.loot.name}.` : result.success ? 'Clean read.' : 'Read the replay.'}</h2>
      <p>{result.insight}</p>
      <div className="result-stats"><span><small>ENTRY</small><b>{money.format(result.entryPrice)}</b></span><span><small>RESOLVE</small><b>{result.resolvePrice ? money.format(result.resolvePrice) : 'REFUND'}</b></span><span><small>P&amp;L</small><b>{formatResultProfitLabel(result) ?? '—'}</b></span><span><small>SWIM</small><b>+{result.settlement?.swimVolumeReward ?? '0'}</b></span></div>
      <div className="result-actions">{shareable && <button className="secondary-button" onClick={() => void shareCatch().catch(() => undefined)}><Share2 /> Share catch</button>}<button className="secondary-button" onClick={onExit}>Lobby</button><button className="primary-button" onClick={onReplay} disabled={!canReplay}><Zap /> Dive again</button></div>
    </div>
  )
}

function RaidArena({ profile, raid, now, busy, onBack, onJoin, onPredict }: {
  profile?: PlayerProfile
  raid?: RaidState
  now: number
  busy: boolean
  onBack: () => void
  onJoin: () => void
  onPredict: (checkpoint: 0 | 1 | 2, direction: MarketDirection) => void
}) {
  if (!raid) return <ArenaLoading onBack={onBack} />
  const activeCheckpoint = raid.checkpoints.find((checkpoint) => !checkpoint.resolved)
  const timeTarget = raid.phase === 'cooldown' ? raid.checkInAt : raid.phase === 'check-in' ? raid.startsAt : raid.endsAt
  const hp = Math.max(0, raid.bossHp / raid.bossMaxHp * 100)
  return (
    <div className="arena-panel raid-arena">
      <div className="arena-heading">
        <button className="icon-button" onClick={onBack} aria-label="Exit raid"><ArrowLeft /></button>
        <div><p className="eyebrow">WHALE RAID · {raid.participantCount} DIVERS</p><h1>{raid.phase === 'cooldown' ? 'SONAR DETECTED' : raid.phase === 'check-in' ? 'CHECK-IN OPEN' : raid.phase === 'active' ? 'BREAK THE ABYSS WHALE' : raid.defeated ? 'WHALE DEFEATED' : 'WHALE ESCAPED'}</h1></div>
        <div className="round-clock"><Clock3 /><strong>{secondsUntil(timeTarget, now)}</strong><small>{raid.phase.toUpperCase()}</small></div>
      </div>
      <div className="boss-hud"><div><span>ABYSS WHALE · {raid.reservedReward || raid.projectedReward} SWIM</span><strong>{number.format(raid.bossHp)} / {number.format(raid.bossMaxHp)} HP</strong></div><div className="boss-health"><span style={{ width: `${hp}%` }} /></div><small>YOUR CONTRIBUTION {raid.contribution} · SPLIT 20% EQUAL / 70% DAMAGE / 10% ACCURACY</small></div>
      {!raid.joined ? (
        <div className="raid-callout"><ShieldCheck /><h2>Every diver deals damage.</h2><p>Make three Up / Range / Down calls. Wrong calls still chip the boss. Entry is charged once and 80% recycles into the Raid pool.</p><button className="primary-button" disabled={busy || !['check-in', 'active'].includes(raid.phase)} onClick={onJoin}>{profile ? 'JOIN RAID' : 'SIGN IN TO JOIN'} <span>{raid.entryFee} SWIM</span></button></div>
      ) : (
        <div className="checkpoint-board">
          {raid.checkpoints.map((checkpoint) => (
            <div className={`checkpoint ${checkpoint.resolved ? checkpoint.void ? 'void' : 'resolved' : activeCheckpoint?.index === checkpoint.index ? 'active' : ''}`} key={checkpoint.index}>
              <span>CHECKPOINT {checkpoint.index + 1}</span><strong>{checkpoint.resolved ? checkpoint.void ? 'VOID' : checkpoint.outcome?.toUpperCase() : activeCheckpoint?.index === checkpoint.index ? secondsUntil(checkpoint.resolveAt, now) : 'QUEUED'}</strong>
            </div>
          ))}
          {raid.phase === 'active' && activeCheckpoint && (
            <div className="direction-controls">
              {(['up', 'range', 'down'] as MarketDirection[]).map((direction) => {
                const { label, Icon } = directionMeta(direction)
                return <button className={direction} disabled={busy || activeCheckpoint.resolveAt - now <= 3_000} key={direction} onClick={() => onPredict(activeCheckpoint.index, direction)}><Icon /><strong>{label}</strong><small>{direction === 'up' ? 'Momentum rises' : direction === 'down' ? 'Momentum falls' : 'Stays in range'}</small></button>
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AquaDex({ profile, species, onBack }: { profile?: PlayerProfile; species: SpeciesDefinition[]; onBack: () => void }) {
  const collection = profile?.collection ?? {}
  const collectionCount = Object.keys(collection).length
  return (
    <div className="dex-panel">
      <div className="arena-heading"><button className="icon-button" onClick={onBack} aria-label="Return to lobby"><ArrowLeft /></button><div><p className="eyebrow">COLLECTION · {collectionCount}/12 DISCOVERED</p><h1>AQUADEX</h1></div><div className="round-clock"><BookOpen /><strong>{collectionCount}</strong><small>SPECIES</small></div></div>
      <p className="dex-intro">Each creature embodies a market behavior. Collection unlocks lore and insights only — never an accuracy advantage.</p>
      <div className="species-grid">
        {species.filter((creature) => !creature.boss).map((creature, index) => {
          const caught = collection[creature.id]
          return <article className={`species-card ${caught ? 'caught' : 'unknown'} ${creature.rarity}`} key={creature.id}><div className="species-portrait"><Fish style={{ color: creature.color }} /><span>{String(index + 1).padStart(2, '0')}</span></div><div><small>{caught ? creature.rarity.toUpperCase() : 'UNDISCOVERED'}</small><h2>{caught ? creature.name : 'Unknown Current'}</h2><p>{caught ? creature.insight : 'Catch this signal in a successful dive to decode its market behavior.'}</p></div>{caught && <b>×{caught.count}</b>}</article>
        })}
      </div>
    </div>
  )
}

function RewardsHub({ profile, onBack, onLogin }: { profile?: PlayerProfile; onBack: () => void; onLogin: () => void }) {
  const [referral, setReferral] = useState<ReferralProgress>()
  const [quests, setQuests] = useState<SocialQuest[]>([])
  const [claims, setClaims] = useState<QuestClaim[]>([])
  const [loading, setLoading] = useState(Boolean(profile))
  const [submitting, setSubmitting] = useState<string>()
  const [message, setMessage] = useState<string>()

  useEffect(() => {
    if (!profile) return
    let cancelled = false
    setLoading(true)
    Promise.all([api.referral(), api.quests(), api.questClaims()])
      .then(([nextReferral, nextQuests, nextClaims]) => {
        if (cancelled) return
        setReferral(nextReferral)
        setQuests(nextQuests)
        setClaims(nextClaims)
      })
      .catch((error) => { if (!cancelled) setMessage(error instanceof Error ? error.message : 'Could not load community rewards.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [profile])

  async function submitClaim(event: FormEvent<HTMLFormElement>, questId: string) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    setSubmitting(questId)
    setMessage(undefined)
    try {
      await api.submitQuest(questId, String(data.get('proofUrl') ?? ''), String(data.get('note') ?? ''))
      const [nextQuests, nextClaims] = await Promise.all([api.quests(), api.questClaims()])
      setQuests(nextQuests)
      setClaims(nextClaims)
      setMessage('Proof submitted for admin review.')
      form.reset()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not submit proof.')
    } finally {
      setSubmitting(undefined)
    }
  }

  const referralLink = referral ? `${location.origin}/r/${referral.code}` : ''
  const progress = referral ? Math.min(100, Number(referral.qualifyingVolume) / Math.max(1, Number(referral.targetVolume)) * 100) : 0
  return (
    <section className="rewards-hub">
      <div className="arena-heading rewards-heading"><button className="icon-button" onClick={onBack} aria-label="Back to lobby"><ArrowLeft /></button><div><p className="eyebrow">COMMUNITY CURRENT</p><h1>REWARDS &amp; QUESTS</h1></div><div className="round-clock"><Share2 /><span><strong>SWIM</strong><small>VIRTUAL ONLY</small></span></div></div>
      {!profile ? <div className="rewards-auth"><ShieldCheck /><h2>Sign in to join the current.</h2><p>Referral and quest rewards are ledgered to a verified account.</p><button className="primary-button" onClick={onLogin}>SIGN IN</button></div> : loading ? <div className="arena-loading"><Radio /> Loading community current…</div> : <>
        {message && <div className="community-message" role="status">{message}</div>}
        <div className="rewards-grid">
          <article className="referral-card">
            <div className="reward-card-icon"><Share2 /></div><p className="eyebrow">INVITE A DIVER</p><h2>100 SWIM for you.<br />50 for them.</h2><p>Your friend qualifies after email verification and $10 settled cash volume.</p>
            <div className="referral-link"><code>{referralLink}</code><button onClick={() => void navigator.clipboard.writeText(referralLink).then(() => setMessage('Referral link copied.')).catch(() => setMessage('Copy the link manually.'))}>COPY</button></div>
            <div className="referral-progress"><span><small>YOUR QUALIFYING VOLUME</small><strong>{money.format(Number(referral?.qualifyingVolume ?? 0))} / {money.format(Number(referral?.targetVolume ?? 10))}</strong></span><em>{referral?.rewarded ? 'REWARDED' : `${Math.round(progress)}%`}</em><i><b style={{ width: `${progress}%` }} /></i></div>
          </article>
          <article className="quest-intro-card"><div className="sonar-rings"><Sparkles /></div><p className="eyebrow">PROOF-BASED MISSIONS</p><h2>Signal beyond<br />the abyss.</h2><p>Complete a listed social mission, submit its public URL, and receive SWIM after manual review.</p><span><ShieldCheck /> No file uploads. No hidden odds.</span></article>
        </div>
        <div className="quest-section"><div className="quest-section-title"><div><p className="eyebrow">ACTIVE ROTATION</p><h2>Social quests</h2></div><span>{quests.length} LIVE</span></div><div className="quest-grid">{quests.length === 0 ? <div className="quest-empty"><Clock3 /><strong>No live quest right now.</strong><span>The next community current will surface here.</span></div> : quests.map((quest) => {
          const claim = claims.find((item) => item.questId === quest.id)
          const locked = claim?.status === 'pending' || claim?.status === 'approved'
          return <article className="quest-card" key={quest.id}><div><span className={`quest-status ${claim?.status ?? 'open'}`}>{claim?.status?.toUpperCase() ?? 'OPEN'}</span><strong>+{quest.rewardSwim} SWIM</strong></div><h3>{quest.title}</h3><p>{quest.description}</p><small>{quest.instructions}</small>{claim?.reviewerNote && <blockquote>Reviewer: {claim.reviewerNote}</blockquote>}<form onSubmit={(event) => void submitClaim(event, quest.id)}><input name="proofUrl" type="url" required placeholder="https://… public proof URL" disabled={locked} /><textarea name="note" maxLength={1000} placeholder="Optional note for reviewer" disabled={locked} /><button className="primary-button" disabled={locked || submitting === quest.id}>{claim?.status === 'approved' ? <><Check /> REWARD PAID</> : claim?.status === 'pending' ? 'UNDER REVIEW' : submitting === quest.id ? 'SENDING…' : 'SUBMIT PROOF'}</button></form></article>
        })}</div></div>
      </>}
    </section>
  )
}

function SettingsPanel({ profile, busy, onClose, onChange }: { profile: PlayerProfile; busy: boolean; onClose: () => void; onChange: (patch: Partial<PlayerProfile['settings']>) => void }) {
  const settings = profile.settings
  return (
    <div className="modal-scrim" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="settings-panel" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <div className="settings-heading"><div><p className="eyebrow">DIVE CONTROLS</p><h2 id="settings-title">Accessibility &amp; audio</h2></div><button className="icon-button" onClick={onClose} aria-label="Close settings"><X /></button></div>
        <label className="range-row"><span><AudioLines /> Master volume</span><output>{Math.round(settings.masterVolume * 100)}%</output><input type="range" min="0" max="1" step="0.05" value={settings.masterVolume} disabled={busy} onChange={(event) => onChange({ masterVolume: Number(event.target.value) })} /></label>
        <label className="range-row"><span><Waves /> Ocean ambience</span><output>{Math.round(settings.musicVolume * 100)}%</output><input type="range" min="0" max="1" step="0.05" value={settings.musicVolume} disabled={busy} onChange={(event) => onChange({ musicVolume: Number(event.target.value) })} /></label>
        <label className="range-row"><span><Zap /> Arcade SFX</span><output>{Math.round(settings.sfxVolume * 100)}%</output><input type="range" min="0" max="1" step="0.05" value={settings.sfxVolume} disabled={busy} onChange={(event) => onChange({ sfxVolume: Number(event.target.value) })} /></label>
        <Toggle label="Reduced motion" description="Removes parallax, particles and camera shake." checked={settings.reducedMotion} disabled={busy} onChange={(checked) => onChange({ reducedMotion: checked })} />
        <Toggle label="High contrast" description="Strengthens edges and text contrast." checked={settings.highContrast} disabled={busy} onChange={(checked) => onChange({ highContrast: checked })} />
        <Toggle label="Color assist" description="Keeps arrows and labels alongside market colors." checked={settings.colorAssist} disabled={busy} onChange={(checked) => onChange({ colorAssist: checked })} />
        <p className="settings-note"><ShieldCheck /> Game outcomes always come from the server's BTC feed. Accessibility choices never change odds.</p>
      </section>
    </div>
  )
}

function Toggle({ label, description, checked, disabled, onChange }: { label: string; description: string; checked: boolean; disabled: boolean; onChange: (checked: boolean) => void }) {
  return <label className="toggle-row"><span><strong>{label}</strong><small>{description}</small></span><input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} /><i aria-hidden="true" /></label>
}

function ArenaLoading({ onBack }: { onBack: () => void }) {
  return <div className="arena-loading"><button className="icon-button" onClick={onBack}><ArrowLeft /></button><Radio /><p>Mapping the live current…</p></div>
}
